<script setup lang="ts">
import { computed } from "vue"

export type UiStepperItem = {
  key: string
  label: string
  description?: string
}

const props = withDefaults(
  defineProps<{
    steps: UiStepperItem[]
    currentStep: number
    interactive?: boolean
  }>(),
  {
    interactive: true,
  }
)

const emit = defineEmits<{
  "update:current-step": [step: number]
}>()

const maxStepIndex = computed(() => Math.max(0, props.steps.length - 1))

function isDone(index: number) {
  return index < props.currentStep
}

function isCurrent(index: number) {
  return index === props.currentStep
}

function stepButtonClass(index: number) {
  if (isCurrent(index)) return "border-slate-900 bg-slate-900 text-white"
  if (isDone(index)) return "border-emerald-600 bg-emerald-600 text-white"

  return "border-slate-300 bg-white text-slate-600"
}

function goToStep(index: number) {
  if (!props.interactive) return
  if (index < 0 || index > maxStepIndex.value) return

  emit("update:current-step", index)
}
</script>

<template>
  <nav aria-label="Etapas do formulario" class="rounded-md border border-slate-200 bg-slate-50 p-3">
    <ol class="flex flex-wrap items-start gap-2 md:gap-3">
      <li v-for="(step, index) in steps" :key="step.key" class="flex min-w-0 items-center gap-2">
        <button
          type="button"
          :class="[
            'flex h-8 w-8 items-center justify-center rounded-full border text-xs font-semibold transition',
            stepButtonClass(index),
          ]"
          :aria-current="isCurrent(index) ? 'step' : undefined"
          @click="goToStep(index)"
        >
          {{ index + 1 }}
        </button>

        <div class="min-w-0">
          <p class="truncate text-xs font-semibold text-slate-800">{{ step.label }}</p>
          <p v-if="step.description" class="truncate text-[11px] text-slate-500">{{ step.description }}</p>
        </div>

        <span v-if="index < steps.length - 1" class="mx-1 h-px w-6 bg-slate-300" aria-hidden="true" />
      </li>
    </ol>
  </nav>
</template>

